import React, { memo, useState, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import style from '../styles/keyboard.module.scss';
import { useDeviceType } from '../hooks/useDeviceType';
import { useKeyDisplay } from '../hooks/useKeyDisplay';
import { useKeyboardState } from '../hooks/useKeyboardState';
import { useKeyClickHandler } from '../hooks/useKeyClickHandler';
import KeyboardRow from './KeyboardRow';
import DomainPicker from './DomainPicker';

/**
 * Main keyboard component. Assembles all hooks and renders rows.
 *
 * Redux injects: serverAppTheme, deviceInfo, auth.
 * All other props come from the consumer (44 existing callers).
 *
 * Props interface is identical to the original CustomKeyboard so all
 * existing consumers work without changes.
 */
const KeyboardContainer = props => {
  const {
    // Redux-injected
    serverAppTheme,
    deviceInfo,
    auth,
    // Callbacks
    onClickBtn,
    closeKeyboard,
    setKeyboardType,
    goToSwipe,
    setinputEmpty,
    setIsDoneClick,
    // Data / config
    type,
    typed,
    screenType,
    maxLength,
    isDone,
    isDot,
    isClr,
    zipcodeFlag,
    isOrderSeat,
    isOfflinePay,
    callApi,
    orderSearch,
    isAlphaNumericFlag,
    donebuttontheme,
    // Styling
    customClass,
    btnSize,
    buttonTheme,
    spacebtn,
    tableKeyHeight,
    keyboardHeight,
    isDiscount,
    employeeCheck,
    customizeKeyBoard,
  } = props;

  // Stable device type — only recomputes when deviceInfo changes
  const { isCounter, isDriveThru, isCallCenter } = useDeviceType(deviceInfo);

  // Domain popup state
  const [showDomains, setShowDomains] = useState(false);
  const dotComLongPressedRef = useRef(false);
  const handleLongPress = useCallback(() => {
    dotComLongPressedRef.current = true;
    setShowDomains(true);
  }, []);
  const hideDomainNames = useCallback(() => setShowDomains(false), []);
  const handleDomainClick = useCallback(
    domain => {
      onClickBtn?.(domain);
      setShowDomains(false);
    },
    [onClickBtn],
  );

  // Display map — static entries are module-level constants, dynamic portion
  // only rebuilds on theme/screenType/type/device changes (not on keystrokes)
  const display = useKeyDisplay(serverAppTheme?.primary_text, screenType, type, deviceInfo);

  // Layout state — two-level memo isolates `typed` from full layout rebuild
  const { layout, setLayout, allLayouts } = useKeyboardState({
    type,
    typed,
    isDone,
    isClr,
    zipcodeFlag,
    isDot,
    isOrderSeat,
    isOfflinePay,
    callApi,
    orderSearch,
    screenType,
  });

  // Click handler — stable reference thanks to pure getNextLayout()
  const { handleClick } = useKeyClickHandler({
    layout,
    setLayout,
    allLayouts,
    type,
    maxLength,
    typed,
    setKeyboardType,
    onClickBtn,
    closeKeyboard,
    goToSwipe,
    setinputEmpty,
    isAlphaNumericFlag,
    setIsDoneClick,
    deviceInfo,
    screenType,
  });

  const handleClickGuarded = useCallback(f => {
    if (f === '.com' && dotComLongPressedRef.current) {
      dotComLongPressedRef.current = false;
      return;
    }
    handleClick(f);
  }, [handleClick]);

  // Keyboard background — only changes on theme change
  const keyboardBgStyle = useMemo(
    () => ({ backgroundColor: serverAppTheme?.keyBoard_Bg_Color }),
    [serverAppTheme?.keyBoard_Bg_Color],
  );

  // Font size class
  const fontSizeClass = useMemo(() => {
    if (customClass?.includes('fs')) return customClass;
    return style.fontSize;
  }, [customClass]);

  const containerClass = cn(
    `col-12 m-0 h-100 ${style.border} ${fontSizeClass} ${style.backgroundColour} ${style.keyboardcontainerHeight} ${customClass || ''}`,
    screenType === 'couponPopUp'
      ? 'px-1 py-0'
      : `p-0 ${type === 'textWithoutNumeric' ? 'py-1' : 'pb-1'}`,
    screenType === 'textreceiptPopUp' && 'pl-1 pb-1',
    screenType === 'openDollarPopUp' && 'pb-1 pl-1',
  );

  return (
    <div className={containerClass} style={keyboardBgStyle}>
      {layout?.map((rowString, rowIndex) => (
        <KeyboardRow
          key={rowString.replace(/\s/g, '')}
          rowString={rowString}
          rowIndex={rowIndex}
          type={type}
          screenType={screenType}
          btnSize={btnSize}
          isCounter={isCounter}
          isDriveThru={isDriveThru}
          isCallCenter={isCallCenter}
          layout={layout}
          allLayouts={allLayouts}
          handleClick={handleClickGuarded}
          display={display}
          handleLongPress={handleLongPress}
          hideDomainNames={hideDomainNames}
          serverAppTheme={serverAppTheme}
          buttonTheme={buttonTheme}
          tableKeyHeight={tableKeyHeight}
          keyboardHeight={keyboardHeight}
          isDiscount={isDiscount}
          employeeCheck={employeeCheck}
          spacebtn={spacebtn}
          donebuttontheme={donebuttontheme}
          orderSearch={orderSearch}
          customizeKeyBoard={customizeKeyBoard}
        />
      ))}

      {showDomains && (
        <DomainPicker
          domains={auth?.topLevelDomains}
          onSelect={handleDomainClick}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  serverAppTheme: state.theme.serverAppTheme,
  deviceInfo: state.oauth.deviceInformation,
  auth: state.oauth.authInfo,
});

const KeyboardContainerMemo = memo(KeyboardContainer);

export default connect(mapStateToProps, null)(KeyboardContainerMemo);
